import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    Lock,
    CheckCircle2,
    Play,
    MessageSquare,
    Award,
    Presentation,
    BookOpen,
    Download,
    Clock,
    ArrowRight,
    FileText,
    HelpCircle,
    ChevronUp,
    Bookmark,
} from "lucide-react";
import Quiz from "@/components/Quiz";
import ChapterAssignmentPanel from "@/components/student/ChapterAssignmentPanel";
import LessonQuizPanel from "@/components/student/LessonQuizPanel";
import Discussion from "@/components/Discussion";
import LessonContent from "@/components/LessonContent";
import LessonPdfReader from "@/components/LessonPdfReader";
import LessonSlideshowViewer from "@/components/LessonSlideshowViewer";
import LessonNotesCard from "@/components/LessonNotesCard";
import AskInstructorChat from "@/components/AskInstructorChat";
import useAttendanceHeartbeat from "@/hooks/useAttendanceHeartbeat";
import useFocusedTimeHeartbeat from "@/lib/useFocusedTimeHeartbeat";
import { chapterConfetti, certificateConfetti } from "@/lib/celebrate";
import { toast } from "sonner";
import { RadialProgress } from "@/components/ui/radial-progress";

// ── Motion presets ──────────────────────────────────────────────────────
const CONTAINER = {
    hidden: {},
    show: { transition: { staggerChildren: 0.07, delayChildren: 0.05 } },
};
const SECTION = {
    hidden: { opacity: 0, y: 14 },
    show: {
        opacity: 1,
        y: 0,
        transition: { type: "spring", stiffness: 320, damping: 30 },
    },
};

export default function Learn() {
    const { programId } = useParams();
    const [program, setProgram] = useState(null);
    const [activeChapter, setActiveChapter] = useState(null);
    const [activeLesson, setActiveLesson] = useState(null);
    const [showQuiz, setShowQuiz] = useState(false);
    const [certificateId, setCertificateId] = useState(null);
    const [pdfReaderOpen, setPdfReaderOpen] = useState(false);
    const [slideshowOpen, setSlideshowOpen] = useState(false);
    const [showTop, setShowTop] = useState(false);

    useAttendanceHeartbeat({
        programId,
        lessonId: activeLesson?.id,
        enabled: !!program,
    });

    // Wave B: focused-time ledger. The 60% chapter-unlock gate (diplomas
    // only) consumes the very same heartbeats, so we send them on every
    // lesson page — not just diploma lessons — but the gate itself only
    // applies when the program is a diploma.
    useFocusedTimeHeartbeat(activeLesson?.id, !!program);

    const load = async () => {
        const { data } = await api.get(`/programs/${programId}/learn`);
        setProgram(data);
        if (!activeChapter && data.chapters?.length) {
            const firstUnlocked = data.chapters.find((c) => !c.locked) || data.chapters[0];
            setActiveChapter(firstUnlocked);
            setActiveLesson(firstUnlocked.lessons?.[0] || null);
        } else if (activeChapter) {
            const updated = data.chapters.find((c) => c.id === activeChapter.id);
            if (updated) setActiveChapter(updated);
        }
        if (data.enrollment?.certificate_id) setCertificateId(data.enrollment.certificate_id);
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [programId]);

    // Refresh the program tree whenever the student passes a lesson quiz
    // so the sidebar unlocks the next lesson immediately without needing a
    // full page reload.
    useEffect(() => {
        const handler = () => { load(); };
        window.addEventListener("cco:lesson-quiz-passed", handler);
        return () => window.removeEventListener("cco:lesson-quiz-passed", handler);
        // eslint-disable-next-line
    }, [programId]);

    /**
     * Returns null if the given lesson is reachable, or a locking reason
     * string otherwise. A lesson is locked when any earlier lesson in the
     * same chapter has a per-lesson quiz that the student has NOT passed
     * (80% required). Lessons in later chapters are gated by the existing
     * chapter-level `locked` flag (assignment / chapter quiz / time gate).
     */
    const findLessonLock = (chapter, lesson) => {
        if (!chapter || !lesson) return null;
        for (const l of chapter.lessons || []) {
            if (l.id === lesson.id) break;
            if (l.has_lesson_quiz && !l.lesson_quiz_passed) {
                return {
                    reason: "prev_lesson_quiz",
                    blockingLesson: l,
                };
            }
        }
        return null;
    };

    const tryOpenLesson = (chapter, lesson) => {
        const lock = findLessonLock(chapter, lesson);
        if (lock) {
            toast.error(
                `Pass "${lock.blockingLesson.title}" quiz at 80% or higher before moving to this lesson.`,
            );
            // Jump the student to the blocking lesson so they can take the quiz.
            setActiveLesson(lock.blockingLesson);
            return;
        }
        setActiveLesson(lesson);
    };

    // Show "back to top" button after scrolling 400 px
    useEffect(() => {
        const onScroll = () => setShowTop(window.scrollY > 400);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    // Auto-mark hook — fires from the natural "end of content" event of
    // each renderer (PDF last page, slideshow last slide, video onEnded).
    // No-ops if the lesson is already completed so we never churn the API
    // on re-visits, and only flips state via the shared toggle helper so
    // optimistic UI + toast + ring refresh happen consistently.
    const maybeAutoMark = (lesson) => {
        if (!lesson?.id) return;
        if (lesson.completed) return;
        toggleLessonComplete(lesson);
    };

    const toggleLessonComplete = async (lesson) => {

        if (!lesson?.id) return;
        const wasCompleted = !!lesson.completed;
        // Optimistic update in local state so the UI reacts instantly.
        setActiveLesson((l) => (l ? { ...l, completed: !wasCompleted } : l));
        setProgram((prev) => {
            if (!prev) return prev;
            const chapters = (prev.chapters || []).map((ch) => ({
                ...ch,
                lessons: (ch.lessons || []).map((l) =>
                    l.id === lesson.id ? { ...l, completed: !wasCompleted } : l,
                ),
            }));
            const delta = wasCompleted ? -1 : 1;
            const summary = prev.progress_summary || {};
            return {
                ...prev,
                chapters,
                progress_summary: {
                    ...summary,
                    lessons_completed: Math.max(
                        0,
                        (summary.lessons_completed || 0) + delta,
                    ),
                },
            };
        });
        try {
            if (wasCompleted) {
                await api.delete(`/lessons/${lesson.id}/complete`);
                toast.success("Lesson marked as not complete.");
            } else {
                await api.post(`/lessons/${lesson.id}/complete`);
                toast.success("Lesson complete! 🎉");
            }
            // Re-sync after the mutation so any server-side derived state
            // (e.g., chapter unlocks) lands authoritatively.
            await load();
        } catch (err) {
            // Roll back on failure.
            setActiveLesson((l) => (l ? { ...l, completed: wasCompleted } : l));
            toast.error("Could not save your progress. Try again.");
        }
    };

    const handleQuizResult = async (result) => {        if (result.passed) {
            chapterConfetti();
            toast.success(`Passed! Score: ${result.score}/${result.total}`);
            if (result.certificate_id) {
                setCertificateId(result.certificate_id);
                setTimeout(() => certificateConfetti(), 400);
                toast.success("Program complete! Certificate issued.");
            }
        } else {
            const need = result.pass_mark || Math.ceil((result.total || 20) * 0.7);
            toast.error(
                `Score: ${result.score}/${result.total}. ${need} required. Try again.`,
            );
        }
        setShowQuiz(false);
        await load();
    };

    const downloadChapterNotes = async () => {
        if (!activeChapter?.id) return;
        try {
            const { data } = await api.get(
                `/chapters/${activeChapter.id}/my-notes/download?format=md`,
                { responseType: "blob" },
            );
            const url = URL.createObjectURL(data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${(activeChapter.title || "chapter").replace(
                /[^A-Za-z0-9._ -]/g,
                "",
            )} notes.md`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch {
            /* silent */
        }
    };

    if (!program) {
        return (
            <div className="max-w-7xl mx-auto px-6 py-8" data-testid="learn-loading">
                Loading...
            </div>
        );
    }

    const chapters = program.chapters || [];
    const completedChapters = chapters.filter((c) => c.passed).length;
    const progressPct = chapters.length ? (completedChapters / chapters.length) * 100 : 0;
    const lessonIsLastInChapter =
        activeChapter &&
        activeLesson &&
        activeLesson.id ===
            activeChapter.lessons[activeChapter.lessons.length - 1]?.id;

    return (
        <motion.div
            variants={CONTAINER}
            initial="hidden"
            animate="show"
            className="max-w-[1280px] mx-auto px-5 sm:px-8 py-6 lg:py-10"
            data-testid="learn-view"
        >
            {/* Top bar */}
            <motion.div variants={SECTION}>
                <Link
                    to="/dashboard"
                    className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1.5"
                >
                    ← Back to dashboard
                </Link>
            </motion.div>

            {/* Program header */}
            <motion.div variants={SECTION} className="mt-6">
                <div className="flex items-start justify-between gap-6 flex-wrap">
                    <div className="min-w-0 flex-1">
                <div className="text-[11px] uppercase tracking-[0.22em] text-accent font-semibold">
                    Program
                </div>
                <h1
                    className="font-display text-3xl sm:text-4xl lg:text-5xl text-primary tracking-tight mt-2 leading-[1.05]"
                    data-testid="program-title"
                >
                    {program.title}
                </h1>
                <div className="mt-4 flex items-center gap-4 max-w-xl">
                    <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {completedChapters} / {chapters.length} chapters complete
                    </span>
                    <div className="flex-1 h-1.5 rounded-full bg-primary/5 overflow-hidden relative">
                        <motion.div
                            className="h-full rounded-full shimmer-bar"
                            initial={{ width: 0 }}
                            animate={{ width: `${progressPct}%` }}
                            transition={{ duration: 0.7, ease: "easeOut", delay: 0.2 }}
                            style={{
                                background:
                                    "linear-gradient(90deg, #D4A140 0%, #E8C07A 50%, #D4A140 100%)",
                                backgroundSize: "200% 100%",
                            }}
                        />
                    </div>
                    <span className="text-sm text-primary font-medium tabular-nums">
                        {Math.round(progressPct)}%
                    </span>
                </div>
                    </div>
                    {program?.progress_summary ? (
                        <div className="shrink-0">
                            <RadialProgress
                                size={140}
                                lessonsCompleted={program.progress_summary.lessons_completed || 0}
                                totalLessons={program.progress_summary.total_lessons || 0}
                                chaptersCompleted={program.progress_summary.chapters_completed || 0}
                                totalChapters={program.progress_summary.total_chapters || 0}
                                flagged={program.progress_summary.flagged_attempts || 0}
                                testId="learn-progress-ring"
                            />
                        </div>
                    ) : null}
                </div>
                {certificateId && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ type: "spring", stiffness: 320, damping: 28, delay: 0.3 }}
                        className="mt-6 max-w-xl p-4 rounded-xl border border-accent/40 bg-accent/10 flex items-center justify-between"
                    >
                        <div className="flex items-center gap-3">
                            <Award className="h-6 w-6 text-accent" />
                            <div>
                                <div className="font-display text-lg text-primary">
                                    Program complete
                                </div>
                                <div className="text-sm text-muted-foreground">
                                    Your certificate is ready.
                                </div>
                            </div>
                        </div>
                        <Button asChild className="bg-primary hover:bg-primary/90" data-testid="view-cert-btn">
                            <Link to={`/certificate/${certificateId}`}>View certificate</Link>
                        </Button>
                    </motion.div>
                )}
            </motion.div>

            {/* Access window banner — shown when the student's enrolment is
                outside its start/end window. Lessons remain readable but
                quiz submissions are blocked at the API; we keep the UI
                gentle so the student knows what's happening. */}
            {program?.access_window && program.access_window.state !== "ok" && (
                <div
                    className={`mt-6 p-4 rounded-xl border flex items-start gap-3 ${
                        program.access_window.state === "expired"
                            ? "border-amber-500/50 bg-amber-50/70 text-amber-900"
                            : "border-sky-500/40 bg-sky-50/70 text-sky-900"
                    }`}
                    data-testid="learn-window-banner"
                >
                    <Clock className="h-5 w-5 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                        <div className="font-display text-base">
                            {program.access_window.state === "expired"
                                ? `Your access window closed on ${program.access_window.end_date}`
                                : `Your access starts on ${program.access_window.start_date}`}
                        </div>
                        <div className="text-xs mt-0.5 opacity-90">
                            {program.access_window.state === "expired"
                                ? "You can still review your notes, but quiz submissions are paused. Request more time from your profile to keep going."
                                : "You can browse the program, but quiz submissions only open from the start date."}
                        </div>
                    </div>
                    {program.access_window.state === "expired" && (
                        <Button
                            asChild
                            size="sm"
                            variant="outline"
                            className="border-amber-600 text-amber-900 hover:bg-amber-100/80"
                            data-testid="learn-request-extension-btn"
                        >
                            <Link to="/me/profile">Request more time</Link>
                        </Button>
                    )}
                </div>
            )}

            {/* Grid */}
            <div className="mt-7 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-7 items-start">
                {/* LEFT COLUMN */}
                <main>
                    {activeLesson ? (
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeLesson.id}
                                variants={CONTAINER}
                                initial="hidden"
                                animate="show"
                                exit={{ opacity: 0, transition: { duration: 0.12 } }}
                                className="space-y-7"
                            >
                                {/* Lesson hero (ivory) */}
                                <motion.section
                                    variants={SECTION}
                                    className="relative overflow-hidden rounded-2xl border border-primary/10 p-6 sm:p-8"
                                    style={{ background: "#FAF7EF" }}
                                    data-testid="lesson-hero"
                                >
                                    {/* Gold bookmark ornament */}
                                    <div
                                        aria-hidden
                                        className="pointer-events-none absolute top-0 right-10 w-7 h-12 rounded-b-sm"
                                        style={{
                                            background:
                                                "linear-gradient(180deg, #D4A140 0%, #E8C07A 100%)",
                                            clipPath:
                                                "polygon(0 0, 100% 0, 100% 100%, 50% 78%, 0 100%)",
                                            opacity: 0.9,
                                        }}
                                    />
                                    {/* Soft gold glow */}
                                    <div
                                        aria-hidden
                                        className="pointer-events-none absolute -top-10 -right-10 w-56 h-56 rounded-full blur-3xl"
                                        style={{ background: "rgba(212,161,64,0.18)" }}
                                    />

                                    <div className="relative z-10">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-accent/15 text-accent text-[10px] uppercase tracking-[0.2em] font-semibold">
                                                {activeChapter?.title}
                                            </span>
                                            {activeChapter?.lessons?.length && (
                                                <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-medium">
                                                    · Lesson{" "}
                                                    {activeChapter.lessons.findIndex(
                                                        (l) => l.id === activeLesson.id,
                                                    ) + 1}{" "}
                                                    of {activeChapter.lessons.length}
                                                </span>
                                            )}
                                        </div>
                                        <h2 className="font-display text-3xl sm:text-4xl text-primary mt-4 leading-tight max-w-2xl">
                                            {activeLesson.title}
                                        </h2>

                                        {activeLesson.content_type === "slideshow" ||
                                        activeLesson.content_type === "pdf-attachment" ? (
                                            <p className="mt-4 text-sm text-muted-foreground max-w-xl leading-relaxed">
                                                {activeLesson.content_type === "slideshow"
                                                    ? `Page-by-page presentation${activeLesson.slide_count ? ` · ${activeLesson.slide_count} slides` : ""}. Snip any passage to add it to your notes, summarize with AI, or have it read aloud.`
                                                    : "Original textbook PDF. Read inline or download for offline."}
                                            </p>
                                        ) : null}

                                        {/* Pills */}
                                        <div className="flex flex-wrap gap-2 mt-5">
                                            {activeLesson.reading_time_minutes ? (
                                                <span
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/5 text-primary/80 text-xs font-medium"
                                                    data-testid="lesson-reading-time"
                                                >
                                                    <Clock className="h-3 w-3" />
                                                    ≈ {activeLesson.reading_time_minutes} min read
                                                </span>
                                            ) : null}
                                            {activeLesson.slide_count ? (
                                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary/5 text-primary/80 text-xs font-medium">
                                                    <Presentation className="h-3 w-3" />
                                                    {activeLesson.slide_count} slides
                                                </span>
                                            ) : null}
                                        </div>

                                        {/* CTA */}
                                        <div className="mt-7">
                                            {activeLesson.content_type === "slideshow" ? (
                                                <>
                                                    <Button
                                                        size="lg"
                                                        onClick={() => setSlideshowOpen(true)}
                                                        className="group bg-primary hover:bg-primary/90 gap-2.5 shadow-lg shadow-primary/20"
                                                        data-testid="lesson-slideshow-open-btn"
                                                    >
                                                        <Play className="h-4 w-4" />
                                                        Open slideshow
                                                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                                                    </Button>
                                                    <div className="text-xs text-muted-foreground mt-3.5">
                                                        Tip: ← / → arrow keys to navigate ·{" "}
                                                        <kbd className="px-1.5 py-0.5 bg-primary/5 rounded text-[10px]">F</kbd>{" "}
                                                        fullscreen ·{" "}
                                                        <kbd className="px-1.5 py-0.5 bg-primary/5 rounded text-[10px]">S</kbd>{" "}
                                                        snip text
                                                    </div>
                                                </>
                                            ) : activeLesson.content_type === "pdf-attachment" ? (
                                                <Button
                                                    size="lg"
                                                    onClick={() => setPdfReaderOpen(true)}
                                                    className="group bg-primary hover:bg-primary/90 gap-2.5 shadow-lg shadow-primary/20"
                                                    data-testid="lesson-pdf-read-btn"
                                                >
                                                    <BookOpen className="h-4 w-4" />
                                                    Read in reader
                                                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                                                </Button>
                                            ) : null}
                                        </div>

                                        {/* Inline video or markdown content for simple lessons */}
                                        {activeLesson.video_url ? (
                                            <video
                                                src={activeLesson.video_url}
                                                controls
                                                onEnded={() => maybeAutoMark(activeLesson)}
                                                className="w-full rounded-md border border-border bg-black aspect-video mt-7"
                                                data-testid="student-lesson-video"
                                            />
                                        ) : null}
                                        {activeLesson.content &&
                                        activeLesson.content_type !== "slideshow" &&
                                        activeLesson.content_type !== "pdf-attachment" ? (
                                            <div className="mt-7">
                                                <LessonContent content={activeLesson.content} />
                                            </div>
                                        ) : null}

                                        {/* "Mark complete" CTA — bottom of every lesson body. */}
                                        <div className="mt-10 pt-6 border-t border-primary/10 flex items-center justify-between flex-wrap gap-3">
                                            <div className="text-xs text-muted-foreground">
                                                {activeLesson.completed
                                                    ? "You marked this lesson as complete. Revisit anytime."
                                                    : "Finished reading? Mark this lesson complete to advance your progress ring."}
                                            </div>
                                            <Button
                                                onClick={() => toggleLessonComplete(activeLesson)}
                                                variant={activeLesson.completed ? "outline" : "default"}
                                                className={
                                                    activeLesson.completed
                                                        ? "border-emerald-500/50 text-emerald-700 hover:bg-emerald-50 gap-2"
                                                        : "bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
                                                }
                                                data-testid={`mark-lesson-complete-${activeLesson.id}`}
                                            >
                                                <CheckCircle2 className="h-4 w-4" />
                                                {activeLesson.completed
                                                    ? "Completed · undo"
                                                    : "Mark as complete"}
                                            </Button>
                                        </div>

                                        {/* Per-lesson practice quiz (only shows if one has been authored). */}
                                        <LessonQuizPanel lesson={activeLesson} />
                                    </div>
                                </motion.section>

                                {/* Notes (apricot) */}
                                <motion.section
                                    variants={SECTION}
                                    className="relative overflow-hidden rounded-2xl border p-6 sm:p-8"
                                    style={{
                                        background: "#FDF5EA",
                                        borderColor: "rgba(212, 161, 64, 0.2)",
                                    }}
                                    data-testid="lesson-notes-section"
                                >
                                    <div
                                        className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] font-semibold mb-2"
                                        style={{ color: "#D4A140" }}
                                    >
                                        <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
                                        My notes
                                    </div>
                                    <div className="flex items-start justify-between gap-3 mb-2">
                                        <h3 className="font-display text-xl sm:text-2xl text-primary">
                                            Jot down what matters
                                        </h3>
                                        <button
                                            type="button"
                                            onClick={downloadChapterNotes}
                                            className="inline-flex items-center gap-1.5 text-xs text-primary/80 hover:text-primary transition bg-white border border-accent/25 rounded-lg px-3 py-1.5 flex-shrink-0"
                                            title="Download all your notes for this chapter as Markdown"
                                            data-testid="chapter-notes-download"
                                        >
                                            <Download className="h-3.5 w-3.5" />
                                            <span className="hidden sm:inline">Download chapter notes</span>
                                        </button>
                                    </div>
                                    <LessonNotesCard
                                        lesson={activeLesson}
                                        userRole="student"
                                        variant="inline"
                                    />
                                </motion.section>

                                {/* Attachments (sage) */}
                                {Array.isArray(activeLesson.attachments) &&
                                activeLesson.attachments.length > 0 ? (
                                    <motion.section
                                        variants={SECTION}
                                        className="relative overflow-hidden rounded-2xl border p-6 sm:p-8"
                                        style={{
                                            background: "#F0F4EE",
                                            borderColor: "rgba(79, 107, 82, 0.16)",
                                        }}
                                        data-testid="lesson-attachments"
                                    >
                                        <div
                                            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] font-semibold mb-2"
                                            style={{ color: "#4F6B52" }}
                                        >
                                            <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
                                            Files
                                        </div>
                                        <h3 className="font-display text-xl sm:text-2xl text-primary mb-4">
                                            Attachments
                                        </h3>
                                        <ul className="space-y-2.5">
                                            {activeLesson.attachments.map((a, idx) => (
                                                <motion.li
                                                    key={idx}
                                                    initial={{ opacity: 0, y: 6 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: idx * 0.04 }}
                                                    whileHover={{ y: -1 }}
                                                    className="flex items-center gap-3 rounded-xl bg-white border px-4 py-3 transition-shadow hover:shadow-md"
                                                    style={{
                                                        borderColor: "rgba(79, 107, 82, 0.14)",
                                                    }}
                                                    data-testid={`lesson-attachment-${idx}`}
                                                >
                                                    <span
                                                        className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0"
                                                        style={{
                                                            background: "rgba(79, 107, 82, 0.1)",
                                                            color: "#4F6B52",
                                                        }}
                                                    >
                                                        <FileText className="h-4 w-4" />
                                                    </span>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-sm font-medium text-primary truncate">
                                                            {a.name}
                                                        </div>
                                                        {(a.size_bytes || a.content_type) && (
                                                            <div className="text-[11px] text-muted-foreground mt-0.5">
                                                                {a.size_bytes
                                                                    ? `${(a.size_bytes / 1024 / 1024).toFixed(1)} MB`
                                                                    : ""}
                                                                {a.size_bytes && a.content_type
                                                                    ? " · "
                                                                    : ""}
                                                                {a.content_type || ""}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <a
                                                        href={a.url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="inline-flex items-center gap-1 text-sm font-medium hover:underline flex-shrink-0"
                                                        style={{ color: "#4F6B52" }}
                                                    >
                                                        <Download className="h-3.5 w-3.5" />
                                                        Download
                                                    </a>
                                                </motion.li>
                                            ))}
                                        </ul>
                                    </motion.section>
                                ) : null}

                                {/* Discussion */}
                                <motion.section
                                    variants={SECTION}
                                    className="relative overflow-hidden rounded-2xl border border-primary/10 bg-card p-6 sm:p-8"
                                    data-testid="lesson-discussion-section"
                                >
                                    <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] font-semibold mb-2 text-primary/70">
                                        <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
                                        Conversation
                                    </div>
                                    <div className="flex items-center gap-2 mb-5">
                                        <MessageSquare className="h-5 w-5 text-accent" />
                                        <h3 className="font-display text-xl sm:text-2xl text-primary">
                                            Discussion
                                        </h3>
                                    </div>
                                    <Discussion lessonId={activeLesson.id} />
                                </motion.section>

                                {/* Quiz CTA at end of chapter */}
                                {activeChapter && activeChapter.has_quiz &&
                                    lessonIsLastInChapter && (
                                    <motion.section
                                        variants={SECTION}
                                        className="relative overflow-hidden rounded-2xl border p-6 sm:p-8"
                                        style={{
                                            background: "linear-gradient(135deg, #FBF5E8 0%, #F8EFD6 100%)",
                                            borderColor: "rgba(212, 161, 64, 0.35)",
                                        }}
                                        data-testid="chapter-quiz-cta"
                                    >
                                        <div className="flex items-start justify-between gap-4 flex-wrap">
                                            <div className="flex-1 min-w-0">
                                                <div
                                                    className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] font-semibold mb-2"
                                                    style={{ color: "#D4A140" }}
                                                >
                                                    <HelpCircle className="h-3 w-3" />
                                                    Chapter quiz
                                                </div>
                                                <div className="font-display text-xl text-primary">
                                                    Ready for the {activeChapter.quiz_question_count}
                                                    -question quiz?
                                                </div>
                                                <div className="text-sm text-muted-foreground mt-1.5">
                                                    Score at least 70% to unlock the next chapter.
                                                </div>
                                                {activeChapter.best_score !== null && (
                                                    <div className="text-xs text-muted-foreground mt-2">
                                                        Best score: {activeChapter.best_score}/
                                                        {activeChapter.quiz_question_count}
                                                    </div>
                                                )}
                                            </div>
                                            <Button
                                                onClick={() => setShowQuiz(true)}
                                                className="bg-primary hover:bg-primary/90 gap-2 flex-shrink-0"
                                                data-testid="start-quiz-btn"
                                            >
                                                <Play className="h-4 w-4" />
                                                {activeChapter.passed ? "Retake quiz" : "Start quiz"}
                                            </Button>
                                        </div>
                                    </motion.section>
                                )}

                                {/* Chapter assignment — appears once quiz is passed */}
                                {activeChapter.passed && activeChapter.has_assignment && (
                                    <ChapterAssignmentPanel
                                        key={activeChapter.id}
                                        chapter={activeChapter}
                                        onSubmitted={load}
                                    />
                                )}
                            </motion.div>
                        </AnimatePresence>
                    ) : (
                        <div className="text-muted-foreground py-12 text-center">
                            Select a lesson to begin
                        </div>
                    )}
                </main>

                {/* RIGHT COLUMN / porcelain rail */}
                <motion.aside
                    variants={SECTION}
                    className="lg:sticky lg:top-8 rounded-2xl border border-primary/10 p-5 space-y-2.5"
                    style={{ background: "#F8F9FB" }}
                    data-testid="chapter-list"
                >
                    <div className="flex items-center gap-2 mb-3 px-1">
                        <Bookmark className="h-4 w-4 text-accent" />
                        <h3 className="font-display text-lg text-primary">Course map</h3>
                    </div>
                    {chapters.map((ch, i) => {
                        const isCurrent = activeChapter?.id === ch.id;
                        return (
                            <motion.div
                                key={ch.id}
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.05 + i * 0.03 }}
                                whileHover={ch.locked ? { rotate: 0 } : { y: -1 }}
                                className={`rounded-xl border transition-all ${
                                    ch.locked
                                        ? "opacity-55 bg-primary/[0.02]"
                                        : ch.passed
                                            ? "bg-emerald-50/70 border-emerald-300/60 shadow-[0_2px_10px_rgba(79,107,82,0.08)]"
                                            : "bg-white"
                                } ${
                                    isCurrent
                                        ? "border-accent/40 shadow-[0_4px_16px_rgba(212,161,64,0.12)]"
                                        : ch.passed
                                            ? ""
                                            : "border-primary/10 hover:border-primary/20"
                                }`}
                                style={
                                    isCurrent
                                        ? {
                                              background:
                                                  "linear-gradient(180deg, #FFFCF5 0%, #FDF5E7 100%)",
                                          }
                                        : ch.passed && !ch.locked
                                            ? {
                                                  background:
                                                      "linear-gradient(180deg, #F2F9F2 0%, #E5F1E6 100%)",
                                              }
                                            : undefined
                                }
                                data-testid={`chapter-card-${i}`}
                                data-passed={ch.passed ? "true" : "false"}
                            >
                                <button
                                    disabled={ch.locked}
                                    onClick={() => {
                                        setActiveChapter(ch);
                                        setActiveLesson(ch.lessons?.[0] || null);
                                    }}
                                    className="w-full text-left px-4 py-3.5 disabled:cursor-not-allowed"
                                    data-testid={`chapter-btn-${i}`}
                                >
                                    <div className="flex items-center justify-between gap-2 mb-1">
                                        <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">
                                            Chapter {i + 1}
                                            {ch.code ? ` · (${ch.code})` : ""}
                                        </span>
                                        {ch.locked ? (
                                            <motion.span
                                                whileHover={{ rotate: 2 }}
                                                className="inline-flex"
                                            >
                                                <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                                            </motion.span>
                                        ) : ch.passed ? (
                                            <CheckCircle2
                                                className="h-3.5 w-3.5"
                                                style={{ color: "#4F6B52" }}
                                            />
                                        ) : isCurrent ? (
                                            <motion.span
                                                animate={{
                                                    boxShadow: [
                                                        "0 0 0 3px rgba(212,161,64,0.18)",
                                                        "0 0 0 6px rgba(212,161,64,0.04)",
                                                        "0 0 0 3px rgba(212,161,64,0.18)",
                                                    ],
                                                }}
                                                transition={{ duration: 2, repeat: Infinity }}
                                                className="w-2 h-2 rounded-full bg-accent"
                                            />
                                        ) : (
                                            <Play className="h-3.5 w-3.5 text-primary/60" />
                                        )}
                                    </div>
                                    <div className="font-display text-[15px] text-primary leading-snug">
                                        {ch.title}
                                    </div>
                                    {/* Always-on quiz indicator — every chapter ends with a
                                     * 20-question quiz. Visible even when the chapter is locked
                                     * or collapsed so students always see the promise. */}
                                    <div
                                        className="mt-2 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.14em] font-semibold"
                                        style={{
                                            color: ch.passed
                                                ? "#4F6B52"
                                                : ch.has_quiz
                                                ? "#B08530"
                                                : "rgba(14, 30, 58, 0.4)",
                                        }}
                                    >
                                        <HelpCircle className="h-3 w-3" />
                                        {ch.passed
                                            ? "Quiz passed"
                                            : ch.has_quiz
                                            ? `${ch.quiz_question_count || 20}-question quiz`
                                            : "20-question quiz (coming soon)"}
                                    </div>
                                    {/* Wave B: 60% focused-time gate for diplomas. We surface
                                     * the bar on every chapter so students see exactly how
                                     * far they have to go to unlock the next chapter. */}
                                    {ch.required_focused_seconds > 0 ? (
                                        <ChapterTimeBar
                                            focusedSec={ch.focused_seconds || 0}
                                            requiredSec={ch.required_focused_seconds}
                                            met={ch.time_gate_met}
                                            lockedReason={ch.locked_reason}
                                            testId={`chapter-time-bar-${i}`}
                                        />
                                    ) : null}
                                </button>
                                {isCurrent && !ch.locked && (
                                    <div className="px-3 pb-3 space-y-0.5">
                                        {ch.lessons?.map((l, lidx) => {
                                            const isActiveLesson = activeLesson?.id === l.id;
                                            // Locked if any earlier lesson in this chapter has
                                            // an unpassed quiz. First lesson (idx 0) is never
                                            // locked. Passed / active lesson stays clickable.
                                            let lockedByLesson = null;
                                            for (let k = 0; k < lidx; k += 1) {
                                                const prev = ch.lessons[k];
                                                if (prev.has_lesson_quiz && !prev.lesson_quiz_passed) {
                                                    lockedByLesson = prev;
                                                    break;
                                                }
                                            }
                                            const isLocked = !!lockedByLesson;
                                            return (
                                                <button
                                                    key={l.id}
                                                    onClick={() => tryOpenLesson(ch, l)}
                                                    className={`w-full text-left text-[13px] px-2 py-1.5 rounded-md transition-colors flex items-center gap-2 ${
                                                        isActiveLesson
                                                            ? "bg-accent/10 text-primary font-semibold"
                                                            : isLocked
                                                            ? "text-foreground/40 hover:bg-primary/5"
                                                            : "text-foreground/70 hover:bg-primary/5"
                                                    }`}
                                                    data-testid={`lesson-btn-${l.id}`}
                                                    title={
                                                        isLocked
                                                            ? `Pass "${lockedByLesson.title}" quiz first (80%)`
                                                            : undefined
                                                    }
                                                >
                                                    {isLocked ? (
                                                        <Lock
                                                            className="h-3.5 w-3.5 flex-shrink-0 text-foreground/40"
                                                            data-testid={`lesson-locked-icon-${l.id}`}
                                                        />
                                                    ) : l.completed ? (
                                                        <CheckCircle2
                                                            className="h-3.5 w-3.5 flex-shrink-0 text-emerald-600"
                                                            data-testid={`lesson-completed-icon-${l.id}`}
                                                        />
                                                    ) : (
                                                        <span
                                                            className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                                                isActiveLesson
                                                                    ? "bg-accent"
                                                                    : "bg-primary/25"
                                                            }`}
                                                            style={
                                                                isActiveLesson
                                                                    ? {
                                                                          boxShadow:
                                                                              "0 0 0 3px rgba(212,161,64,0.18)",
                                                                      }
                                                                    : undefined
                                                            }
                                                        />
                                                    )}
                                                    <span className="truncate flex-1">
                                                        {l.title}
                                                    </span>
                                                    {l.has_lesson_quiz && l.lesson_quiz_passed ? (
                                                        <CheckCircle2
                                                            className="h-3 w-3 flex-shrink-0 text-emerald-600"
                                                            data-testid={`lesson-quiz-passed-badge-${l.id}`}
                                                        />
                                                    ) : null}
                                                    {l.reading_time_minutes ? (
                                                        <span className="text-[10px] text-muted-foreground tabular-nums flex-shrink-0">
                                                            {l.reading_time_minutes}m
                                                        </span>
                                                    ) : null}
                                                </button>
                                            );
                                        })}
                                        {/* Chapter quiz row — always shown inside the expanded
                                         * chapter so students always know the quiz awaits. When
                                         * the admin hasn't created it yet, the row renders in a
                                         * disabled/coming-soon state. */}
                                        <div
                                            className={`mt-1.5 border-t pt-2`}
                                            style={{
                                                borderColor: "rgba(212,161,64,0.18)",
                                            }}
                                        >
                                            {ch.has_quiz ? (
                                                <button
                                                    onClick={() => {
                                                        const last =
                                                            ch.lessons?.[ch.lessons.length - 1];
                                                        if (last) setActiveLesson(last);
                                                        setTimeout(() => {
                                                            const el = document.querySelector(
                                                                '[data-testid="chapter-quiz-cta"]',
                                                            );
                                                            el?.scrollIntoView({
                                                                behavior: "smooth",
                                                                block: "center",
                                                            });
                                                        }, 150);
                                                    }}
                                                    className={`w-full text-left text-[13px] px-2 py-1.5 rounded-md flex items-center gap-2 transition-colors ${
                                                        ch.passed
                                                            ? "text-foreground/70"
                                                            : "text-primary hover:bg-accent/10"
                                                    }`}
                                                    data-testid={`chapter-quiz-link-${i}`}
                                                >
                                                    <HelpCircle
                                                        className="h-3.5 w-3.5 flex-shrink-0"
                                                        style={{ color: "#D4A140" }}
                                                    />
                                                    <span className="flex-1">
                                                        {ch.passed
                                                            ? "Chapter quiz · passed"
                                                            : `Chapter quiz · ${ch.quiz_question_count || 20} Qs`}
                                                    </span>
                                                    {ch.passed ? (
                                                        <CheckCircle2
                                                            className="h-3.5 w-3.5 flex-shrink-0"
                                                            style={{ color: "#4F6B52" }}
                                                        />
                                                    ) : (
                                                        <span
                                                            className="w-2 h-2 rounded-full flex-shrink-0"
                                                            style={{
                                                                background:
                                                                    "rgba(212,161,64,0.7)",
                                                            }}
                                                        />
                                                    )}
                                                </button>
                                            ) : (
                                                <div
                                                    className="w-full text-left text-[13px] px-2 py-1.5 rounded-md flex items-center gap-2 text-muted-foreground cursor-not-allowed"
                                                    title="Your instructor hasn't published this chapter's quiz yet"
                                                    data-testid={`chapter-quiz-placeholder-${i}`}
                                                >
                                                    <HelpCircle className="h-3.5 w-3.5 flex-shrink-0 opacity-50" />
                                                    <span className="flex-1">
                                                        Chapter quiz · 20 Qs
                                                    </span>
                                                    <span className="text-[10px] uppercase tracking-[0.12em] opacity-70">
                                                        Soon
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </motion.div>
                        );
                    })}
                </motion.aside>
            </div>

            {showQuiz && activeChapter && (
                <Quiz
                    chapterId={activeChapter.id}
                    chapterTitle={activeChapter.title}
                    onClose={() => setShowQuiz(false)}
                    onResult={handleQuizResult}
                />
            )}

            <LessonPdfReader
                open={pdfReaderOpen}
                onOpenChange={setPdfReaderOpen}
                lessonId={activeLesson?.id}
                lessonTitle={activeLesson?.title}
                onReachEnd={() => maybeAutoMark(activeLesson)}
                downloadUrl={
                    (activeLesson?.attachments || []).find(
                        (a) =>
                            (a?.content_type || "").toLowerCase() === "application/pdf",
                    )?.url
                }
            />

            <LessonSlideshowViewer
                open={slideshowOpen}
                onClose={() => setSlideshowOpen(false)}
                lesson={activeLesson}
                onReachEnd={() => maybeAutoMark(activeLesson)}
            />

            {/* Floating back-to-top */}
            <AnimatePresence>
                {showTop && (
                    <motion.button
                        type="button"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 12 }}
                        whileHover={{ y: -2 }}
                        onClick={() =>
                            window.scrollTo({ top: 0, behavior: "smooth" })
                        }
                        className="fixed bottom-24 right-6 z-[60] h-11 w-11 rounded-full bg-primary text-primary-foreground shadow-xl shadow-primary/25 flex items-center justify-center"
                        title="Back to top"
                        data-testid="back-to-top-button"
                    >
                        <ChevronUp className="h-5 w-5" />
                    </motion.button>
                )}
            </AnimatePresence>

            <AskInstructorChat
                programId={programId}
                programTitle={program?.title}
            />
        </motion.div>
    );
}


/**
 * Slim "focused study time" bar shown on each chapter card. Tells the
 * student how much more focused time is required in the current chapter
 * before the next one unlocks (60% rule, diplomas only).
 */
function ChapterTimeBar({ focusedSec, requiredSec, met, lockedReason, testId }) {
    const pct = requiredSec > 0
        ? Math.min(100, Math.round((focusedSec / requiredSec) * 100))
        : 0;
    const focusedMin = Math.floor(focusedSec / 60);
    const requiredMin = Math.ceil(requiredSec / 60);
    const remainingMin = Math.max(0, requiredMin - focusedMin);
    const explainLock = lockedReason === "previous_time_gate_not_met";
    return (
        <div className="mt-3" data-testid={testId}>
            <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.16em] font-semibold mb-1.5">
                <span className={met ? "text-emerald-700" : "text-primary/70"}>
                    {met ? "Time floor met" : `Focused time · ${pct}%`}
                </span>
                <span className="font-mono text-[10px] text-muted-foreground normal-case tracking-normal">
                    {focusedMin}m / {requiredMin}m
                </span>
            </div>
            <div className="h-1 bg-muted rounded-full overflow-hidden">
                <div
                    className={`h-full rounded-full transition-all ${
                        met ? "bg-emerald-500" : "bg-primary"
                    }`}
                    style={{ width: `${pct}%` }}
                />
            </div>
            {explainLock ? (
                <div className="text-[10px] text-amber-700 mt-1.5 leading-snug">
                    The next chapter unlocks after another {remainingMin}m of focused
                    study here (60% of {requiredMin}m allotted).
                </div>
            ) : null}
        </div>
    );
}
